import { faSolarPanel } from '@fortawesome/free-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { CircularProgress, Tooltip, Typography } from '@mui/material';
import MeioJulgamentoProcesso from '@/dominio/MeioJulgamentoProcesso';
import SituacaoVoto, { SITUACAO_VOTO } from '@/dominio/SituacaoVoto';
import TipoRemessaJulgamentoProcesso from '@/dominio/TipoRemessaJulgamentoProcesso';
import { action, computed, makeObservable, observable } from 'mobx';
import { inject, observer } from 'mobx-react';
import Configuracao from '@/util/Configuracao';
import DataUtil from '@/util/DataUtil';
import NumeroProcessoUtil from '@/util/NumeroProcessoUtil';
import SystemUtil from '@/util/SystemUtil';
import ReactComponent from '../ReactComponent';
import { ref } from './FerramentasListaProcessos';
import { SITUACAO_INSCRICAO_ADVOGADO } from '@/dominio/SituacaoInscricaoAdvogado';
import { MODO_PARTICIPACAO_ADVOGADO } from '@/dominio/ModoParticipacaoAdvogado';

class ExportarPainelVotacaoSessaoParaArquivoCsv extends ReactComponent {
    /**
     * @desc Separador CSV para o arquivo a ser exportado.
     * @static
     * @readonly
     * @constant {string}
     */
    static SEPARADOR_CSV = ';';

    gerandoArquivo = false;

    constructor(props) {
        super(props);

        makeObservable(this, {
            gerandoArquivo: observable,
            handleClickExportarLista: action,
            nomeArquivoCsv: computed,
            cabecalhoMagistradosSessao: computed,
            dados: computed,
            cabecalhos: computed,
            conteudoCsv: computed,
            temFiltroAtivo: computed
        });
    }

    /**
     * @desc Exporta a lista.
     */
    handleClickExportarLista = async () => {
        this.gerandoArquivo = true;
        SystemUtil.exportarAquivoCsv(this.nomeArquivoCsv, this.conteudoCsv);
        this.gerandoArquivo = false;
    };

    get nomeArquivoCsv() {
        return this.sessaoStore?.montarNomeArquivoExportacao('votos');
    }

    get cabecalhoMagistradosSessao() {
        const composicaoSessaoParaProcessosSelecionados = new Set();

        if (this.store.processosFiltrados) {
            this.store.processosFiltrados.forEach((processoSelecionado) =>
                processoSelecionado.composicao.forEach((comp) =>
                    composicaoSessaoParaProcessosSelecionados.add(comp.magistrado.sigla)
                )
            );
        }

        return Array.from(composicaoSessaoParaProcessosSelecionados).sort();
    }

    /**
     * Contabiliza as presenças dos modos de participação indicados
     * @param {Object} processo Objeto processo
     * @param {Set<string>} codigosModosParticipacao Códigos dos modos de participação dos advogados
     * @returns {{totalAusentes: number, totalPresentes: number, totalNA: number}}
     */
    contabilizarPresencas(processo, codigosModosParticipacao) {
        let totalPresentes = 0;
        let totalAusentes = 0;
        let totalNA = 0;

        if (
            processo.pedidoSustentacaoOral?.pedidosDetalhados &&
            processo.pedidoSustentacaoOral.pedidosDetalhados.length > 0
        ) {
            processo.pedidoSustentacaoOral.pedidosDetalhados.forEach((pedido) => {
                pedido.advogados &&
                    pedido.advogados.forEach((adv) => {
                        if (codigosModosParticipacao.has(adv.modoParticipacao)) {
                            switch (adv.presente) {
                                case SITUACAO_INSCRICAO_ADVOGADO.PRESENTE.codigo:
                                    totalPresentes++;
                                    break;
                                case SITUACAO_INSCRICAO_ADVOGADO.AUSENTE.codigo:
                                    totalAusentes++;
                                    break;
                                case SITUACAO_INSCRICAO_ADVOGADO.INSCRITO.codigo:
                                    totalNA++;
                                    break;
                                default:
                                    break;
                            }
                        }
                    });
            });
        }

        return { totalAusentes, totalPresentes, totalNA };
    }

    contabilizarPresencasNaoVirtuais(processo) {
        const codigosModosParticipacao = new Set([
            MODO_PARTICIPACAO_ADVOGADO.INDEFINIDO.codigo,
            MODO_PARTICIPACAO_ADVOGADO.PRESENCIAL.codigo,
            MODO_PARTICIPACAO_ADVOGADO.TELEPRESENCIAL.codigo
        ]);

        return this.contabilizarPresencas(processo, codigosModosParticipacao);
    }

    contabilizarPresencasTelepresenciais(processo) {
        const codigosModosParticipacao = new Set([MODO_PARTICIPACAO_ADVOGADO.TELEPRESENCIAL.codigo]);

        return this.contabilizarPresencas(processo, codigosModosParticipacao);
    }

    /**
     * @desc Monta os dados para exportação.
     */
    get dados() {
        const processosSelecionados = this.store.processosFiltrados;
        const magistradosSessao = this.cabecalhoMagistradosSessao;
        let index = 0;

        return processosSelecionados.map((processo) => {
            index++;

            const linhaExportacao = {
                sequencial: index,
                sistemaTramitacaoProcessual: processo.processo.origem === Configuracao.ORIGEM_PJE ? 'PJe' : 'eSIJ',
                numeroCompleto:
                    processo.processo.origem === Configuracao.ORIGEM_PJE
                        ? NumeroProcessoUtil.formatarNumeroProcesso(processo.processo.numeroCompleto, true, true)
                        : processo.processo.numeroCompleto,
                relatorSigla: processo?.processo?.relator?.sigla || null,
                composicao: processo.composicao
                    ? processo.composicao.map((comp) => comp.magistrado.sigla).join(', ')
                    : null,
                vistor:
                    Array.isArray(processo?.informacoesAdicionais?.RetornoVistaRegimental) &&
                    processo.informacoesAdicionais.RetornoVistaRegimental.length > 0
                        ? processo.informacoesAdicionais.RetornoVistaRegimental.map((rvr) => rvr.magistrado.sigla).join(
                              ', '
                          )
                        : null,
                impedimentosPossiveis: processo?.impedimentos
                    ? processo.impedimentos
                          .filter((impedimento) => impedimento.situacaoImpedimento === 'IMPEDIMENTO_POSSIVEL')
                          .map((impedimento) => impedimento.magistrado.sigla)
                          .join(', ')
                    : null,
                impedimentosDeclarados: processo?.impedimentos
                    ? processo.impedimentos
                          .filter((impedimento) => impedimento.situacaoImpedimento === 'IMPEDIMENTO_DECLARADO')
                          .map((impedimento) => impedimento.magistrado.sigla)
                          .join(', ')
                    : null,
                segredoJustica: processo.segredoJustica ? 'Sim' : '',
                tipoJulgamento: processo.tipoJulgamento
                    ? MeioJulgamentoProcesso.getDescricaoCurtaByCodigo(processo.tipoJulgamento)
                    : null,
                resultadoJulgamento: processo.resultadoJulgamento?.descricao,
                dataHoraJulgamento: DataUtil.formatarDataHoraParaCsv(processo.dataJulgamento),
                remessa: processo.remessaJulgamento?.tipo?.id
                    ? TipoRemessaJulgamentoProcesso.getDescricao(processo.remessaJulgamento.tipo.id)
                    : null,
                totalAdvPresentes: this.contabilizarPresencasNaoVirtuais(processo).totalPresentes,
                totalAdvPresentesPorVideoconferencia: this.contabilizarPresencasTelepresenciais(processo).totalPresentes
            };

            // Adiciona chaves e valores para cada voto, na linha a ser exportada
            if (processo.votos) {
                magistradosSessao.forEach((magSigla) => {
                    let keyVoto = 'voto_' + magSigla;

                    if (magSigla === processo.processo.relator?.sigla) {
                        linhaExportacao[keyVoto] = 'RELATOR';
                    } else {
                        processo.votos.forEach((voto) => {
                            const siglaDoMagistrado = voto.siglaDoMagistrado;

                            if (siglaDoMagistrado === magSigla) {
                                const descricao = voto.voto?.descricao
                                    ? voto.voto.descricao
                                    : SITUACAO_VOTO.SEM_VOTO.descricao;

                                let descricaoVoto = '';
                                let situacaoVoto;

                                switch (voto?.situacaoVoto) {
                                    case SITUACAO_VOTO.SEM_VOTO.id:
                                    case SITUACAO_VOTO.CONFIRMADO.id:
                                        descricaoVoto = descricao;
                                        break;
                                    case SITUACAO_VOTO.PRE_VOTADO.id:
                                        descricaoVoto = SITUACAO_VOTO.PRE_VOTADO.descricao;
                                        break;
                                    default:
                                        situacaoVoto = voto?.situacaoVoto
                                            ? SituacaoVoto.getDescricao(voto.situacaoVoto)
                                            : undefined;

                                        if (situacaoVoto) {
                                            if (descricao && descricao !== situacaoVoto) {
                                                descricaoVoto = `${descricao} (${situacaoVoto})`;
                                            } else {
                                                descricaoVoto = situacaoVoto;
                                            }
                                        } else {
                                            descricaoVoto = descricao;
                                        }

                                        break;
                                }

                                linhaExportacao[keyVoto] = descricaoVoto;
                            }
                        });
                    }
                });
            } else if (processo.processo?.relator?.sigla) {
                let keyVoto = 'voto_' + processo.processo.relator.sigla;
                linhaExportacao[keyVoto] = 'RELATOR';
            }

            return linhaExportacao;
        });
    }

    /**
     * @desc Monta os cabeçalhos do CSV.
     */
    get cabecalhos() {
        let cabecalhos = [];

        cabecalhos.push(
            { label: 'Seq', key: 'sequencial' },
            { label: 'Sistema', key: 'sistemaTramitacaoProcessual' },
            { label: 'Número Processo', key: 'numeroCompleto' },
            { label: 'Relator', key: 'relatorSigla' },
            { label: 'Composição', key: 'composicao' },
            { label: 'Retorno Vista Reg.', key: 'vistor' },
            { label: 'Possíveis Impedimentos', key: 'impedimentosPossiveis' },
            { label: 'Impedimentos Declarados', key: 'impedimentosDeclarados' },
            { label: 'Segredo de Justiça', key: 'segredoJustica' },
            { label: 'Meio Julgamento', key: 'tipoJulgamento' },
            { label: 'Resultado Julgamento', key: 'resultadoJulgamento' },
            { label: 'Data/Hora Julgamento', key: 'dataHoraJulgamento' },
            { label: 'Remessa', key: 'remessa' },
            { label: 'Total Adv. Presentes', key: 'totalAdvPresentes' },
            { label: 'Total Adv. Presentes Video', key: 'totalAdvPresentesPorVideoconferencia' }
        );

        // Adiciona os cabeçalhos dinamicamente conforme os magistrados que participaram da sessão
        this.cabecalhoMagistradosSessao.forEach((magSigla) => {
            let keyVoto = 'voto_' + magSigla;
            cabecalhos.push({
                label: `Voto - ${magSigla}`,
                key: keyVoto
            });
        });

        return cabecalhos;
    }

    /**
     * @desc Monta o conteúdo do CSV.
     */
    get conteudoCsv() {
        const sep = ExportarPainelVotacaoSessaoParaArquivoCsv.SEPARADOR_CSV;
        const cabecalhoLinha = this.cabecalhos.map((col) => col.label).join(sep);
        const linhas = this.dados.map((linha) =>
            this.cabecalhos.map((col) => `"${(linha[col.key] ?? '').toString().replace(/"/g, '""')}"`).join(sep)
        );
        return [cabecalhoLinha, ...linhas].join('\n');
    }

    get temFiltroAtivo() {
        return Array.isArray(this.store.filtros?.filtrosComValor) && this.store.filtros.filtrosComValor.length > 0;
    }

    render() {
        if (this.gerandoArquivo) {
            return <CircularProgress size="24px" />;
        }

        const filtrados = this.temFiltroAtivo ? ' dos processos filtrados' : ' da sessão';

        return (
            <Tooltip
                onClick={this.handleClickExportarLista}
                title={<Typography>{`Exportar painel de votação${filtrados} para arquivo CSV`}</Typography>}
                placement="top-start"
                ref={ref}
            >
                <div
                    className="corBotao"
                    aria-label={`Exportar painel de votação${filtrados} para arquivo CSV`}
                >
                    <FontAwesomeIcon
                        icon={faSolarPanel}
                        style={{ cursor: 'pointer', width: '22px', height: '22px', marginRight: '10px' }}
                    />
                </div>
            </Tooltip>
        );
    }
}

export default inject('stores')(observer(ExportarPainelVotacaoSessaoParaArquivoCsv));
